import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
} from 'recharts';
import { GAP_TYPES } from '../../data/gapKeywords';

// ── Custom bar tooltip defined outside render to avoid reconciliation issues ──
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="gf-tooltip">
        <strong>{label} Gap</strong>
        {payload.map((p) => (
          <div key={p.dataKey}>
            {p.name}: <strong>{p.value}</strong>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function Step3_MatrixBuilder({ matrixData, onNext, onBack }) {
  if (!matrixData) {
    return (
      <div className="gf-step">
        <p className="gf-hint gf-warn">No analysis data yet. Please complete Phase 2 first.</p>
        <div className="gf-nav">
          <button className="gf-btn gf-btn-secondary" onClick={onBack}>← Back</button>
        </div>
      </div>
    );
  }

  const { matrixRows = [], rankedGaps = [], barData = [], radarData = [] } = matrixData;

  const maxScore = rankedGaps.length > 0
    ? Math.max(...rankedGaps.map((g) => g.totalScore), 1)
    : 1;

  return (
    <div className="gf-step">
      <div className="gf-step-header">
        <span className="gf-step-badge">Phase 3</span>
        <h2>Limitations Matrix &amp; Gap Rankings</h2>
        <p>The engine has analysed all abstracts. Review the matrix and charts below.</p>
      </div>

      {/* ── Ranked gap cards ── */}
      <div className="gf-gap-cards">
        {rankedGaps.slice(0, 3).map((gap, idx) => (
          <div key={gap.type} className="gf-gap-card" style={{ borderLeftColor: gap.color }}>
            <div className="gf-gap-card-rank" style={{ background: gap.color }}>
              #{idx + 1}
            </div>
            <div className="gf-gap-card-body">
              <h3>{gap.label}</h3>
              <p>{GAP_TYPES[gap.type]?.description}</p>
              <div className="gf-gap-meta">
                <span>Score: <strong>{gap.totalScore}</strong></span>
                <span>Articles: <strong>{gap.articleCount}</strong></span>
                <span>Coverage: <strong>{gap.percentage}%</strong></span>
              </div>
              <div className="gf-mini-bar-wrap">
                <div
                  className="gf-mini-bar-fill"
                  style={{ width: `${(gap.totalScore / maxScore) * 100}%`, background: gap.color }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Horizontal Bar Chart ── */}
      <div className="gf-chart-section">
        <h3>Gap Score Rankings (Horizontal Bar)</h3>
        {barData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={barData}
              layout="vertical"
              margin={{ top: 10, right: 60, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 13 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="score" radius={[0, 6, 6, 0]} name="Signal Score">
                {barData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList dataKey="score" position="right" style={{ fontWeight: 700, fontSize: 14 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="gf-hint">No gap scores to display. Try adding more detailed abstracts.</p>
        )}
      </div>

      {/* ── Radar Chart ── */}
      <div className="gf-chart-section">
        <h3>Gap Radar Profile</h3>
        {radarData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={radarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
              <Radar
                name="Gap Strength"
                dataKey="score"
                stroke="#6366f1"
                fill="#6366f1"
                fillOpacity={0.35}
              />
              <Radar
                name="Article Count"
                dataKey="articles"
                stroke="#22c55e"
                fill="#22c55e"
                fillOpacity={0.2}
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        ) : (
          <p className="gf-hint">No radar data to display.</p>
        )}
      </div>

      {/* ── Limitations Matrix Table ── */}
      <div className="gf-chart-section">
        <h3>Limitations Matrix</h3>
        {matrixRows.length > 0 ? (
          <div className="gf-table-wrap">
            <table className="gf-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Citation</th>
                  <th>Title</th>
                  <th>Dominant Gap</th>
                  <th>Key Phrase</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {matrixRows.map((row, i) => (
                  <tr key={row.id || i}>
                    <td>{i + 1}</td>
                    <td className="gf-citation">{row.citation}</td>
                    <td className="gf-title-cell">{row.title}</td>
                    <td>
                      <span
                        className="gf-gap-pill"
                        style={{
                          background: (GAP_TYPES[row.dominantGapType]?.color || '#6366f1') + '22',
                          color: GAP_TYPES[row.dominantGapType]?.color || '#6366f1',
                        }}
                      >
                        {row.dominantGap}
                      </span>
                    </td>
                    <td className="gf-phrase-cell"><em>"{row.keyPhrase}"</em></td>
                    <td>
                      <div className="gf-conf-bar-wrap">
                        <div className="gf-conf-bar-fill" style={{ width: `${row.confidenceScore}%` }} />
                        <span>{row.confidenceScore}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="gf-hint">No articles processed yet.</p>
        )}
      </div>

      <div className="gf-nav">
        <button className="gf-btn gf-btn-secondary" onClick={onBack}>← Back</button>
        <button className="gf-btn gf-btn-primary" onClick={onNext}>
          Confirm Gap Types →
        </button>
      </div>
    </div>
  );
}
